import { Injectable } from '@angular/core';
import { HttpClient, HttpContextToken, HttpParams } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import {
  FormDataSubmission,
  FormOption,
  NewRopa,
  RopaForm,
  RopaNotificationEmail,
  ROPAResponse,
  SearchQuestionOptions,
  ThirdPartyProvider,
  UploadUrlRequest,
  ConfirmUploadResponse,
} from '../models/ropa.interface';
import config from '../../../../config';

@Injectable({
  providedIn: 'root',
})
export class RopaService {
  baseUrl = config.API_GATEWAY_ENDPOINT;

  constructor(private http: HttpClient) {}

  /**
   * Gets the RoPA form structure from the API
   * @param formId - Form ID (optional, default 1)
   * @param includeInactive - Include inactive fields (optional, default false)
   * @returns Promise with RoPA form structure
   */
  getRopaForm(): Promise<RopaForm> {
    return lastValueFrom(
      this.http.get<RopaForm>(
        `${this.baseUrl}/forms/structure?form_id=1&include_inactive=false`
      )
    );
  }

  /**
   * Submits the completed form
   * @param ropaId - RoPA record ID
   * @param formData - Form data to submit
   * @returns Promise with server response
   */
  submitRopaForm(formData: FormDataSubmission): Promise<NewRopa> {
    return lastValueFrom(
      this.http.post<NewRopa>(`${this.baseUrl}/projects`, formData)
    );
  }

  submitDraftRopaForm(formData: FormDataSubmission): Promise<NewRopa> {
    return lastValueFrom(
      this.http.post<NewRopa>(`${this.baseUrl}/drafts`, formData)
    );
  }

  getAllRopas(project_id?: number, version?: number): Promise<ROPAResponse> {
    let params = new HttpParams();
    if (project_id !== undefined) params = params.set('project_id', project_id);
    if (version !== undefined) params = params.set('version', version);
    return lastValueFrom(
      this.http.get<ROPAResponse>(`${this.baseUrl}/projects`, { params })
    );
  }

  getAllDrafts(prid: string): Promise<ROPAResponse> {
    return lastValueFrom(
      this.http.get<ROPAResponse>(
        `${this.baseUrl}/drafts?project_id=&version=&prid=${prid}`
      )
    );
  }

  /**
   * Updates an existing RoPA form
   * @param ropaId - RoPA record ID
   * @param formData - Form data to update
   * @returns Promise with server response
   */
  createNewVersionForm(formData: FormDataSubmission): Promise<NewRopa> {
    return lastValueFrom(
      this.http.post<NewRopa>(`${this.baseUrl}/projects/versions`, formData)
    );
  }

  updateDraft(formData: FormDataSubmission, draft_id: number): Promise<any> {
    return lastValueFrom(
      this.http.put<any>(`${this.baseUrl}/drafts/${draft_id}`, formData)
    );
  }

  deleteDraft(draft_id: number, prid: string): Promise<any> {
    return lastValueFrom(
      this.http.delete<any>(`${this.baseUrl}/drafts/${draft_id}?prid=${prid}`)
    );
  }

  reassignDraft(
    draft_id: number,
    target_user_email: string,
    previous_owner_email: string
  ): Promise<any> {
    return lastValueFrom(
      this.http.post<any>(`${this.baseUrl}/drafts/${draft_id}/reassign`, {
        target_user_email,
        previous_owner_email,
      })
    );
  }

  publishDraft(ropa: string, prid: string, draft_id: number): Promise<any> {
    return lastValueFrom(
      this.http.post<any>(
        `${this.baseUrl}/drafts/${draft_id}/publish?prid=${prid}`,
        { ropa: ropa }
      )
    );
  }

  viewRopa(project_id: string, version: string): Promise<RopaForm> {
    return lastValueFrom(
      this.http.get<RopaForm>(
        `${this.baseUrl}/projects/structure?project_id=${project_id}&version=${version}`
      )
    );
  }

  viewDraft(draft_id: string, prid: string): Promise<RopaForm> {
    return lastValueFrom(
      this.http.get<RopaForm>(
        `${this.baseUrl}/drafts/structure?draft_id=${draft_id}&prid=${prid}`
      )
    );
  }

  downloadRopa(ropaId: number, version: number): Promise<any> {
    // Real API call
    return lastValueFrom(
      this.http.get<any>(`${this.baseUrl}/download`, {
        params: { project_id: ropaId.toString(), version: version.toString() },
        observe: 'response',
      })
    ).catch((error) => {
      console.error('API call failed, using mock response:', error);

      // Fallback to mock response if API fails
      const mockResponse = {
        statusCode: 200,
        headers: {
          'Content-Type': 'application/pdf',
          'Content-Disposition': 'attachment; filename="hola.pdf"',
          'Content-Length': '971',
          'Access-Control-Expose-Headers': 'Content-Disposition',
        },
        body: 'JVBERi0xLjMKMSAwIG9iago8PAovQ291bnQgMQovS2lkcyBbMyAwIFJdCi9NZWRpYUJveCBbMCAwIDU5NS4yOCA4NDEuODldCi9UeXBlIC9QYWdlcwo+PgplbmRvYmoKMiAwIG9iago8PAovT3BlbkFjdGlvbiBbMyAwIFIgL0ZpdEggbnVsbF0KL1BhZ2VMYXlvdXQgL09uZUNvbHVtbgovUGFnZXMgMSAwIFIKL1R5cGUgL0NhdGFsb2cKPj4KZW5kb2JqCjMgMCBvYmoKPDwKL0NvbnRlbnRzIDQgMCBSCi9QYXJlbnQgMSAwIFIKL1Jlc291cmNlcyA2IDAgUgovVHlwZSAvUGFnZQo+PgplbmRvYmoKNCAwIG9iago8PAovRmlsdGVyIC9GbGF0ZURlY29kZQovTGVuZ3RoIDY3Cj4+CnN0cmVhbQp4nDNS8OIy0DM1VyjncgpR0HczVDA00zMwUAhJU3ANAQkZWRjqGVoqmBgY6lmaK4SkKGhk5OckaiqEZIEUAACkDg3qCmVuZHN0cmVhbQplbmRvYmoKNSAwIG9iago8PAovQmFzZUZvbnQgL0hlbHZldGljYS1Cb2xkCi9FbmNvZGluZyAvV2luQW5zaUVuY29kaW5nCi9TdWJ0eXBlIC9UeXBlMQovVHlwZSAvRm9udAo+PgplbmRvYmoKNiAwIG9iago8PAovRm9udCA8PC9GMSA1IDAgUj4+Ci9Qcm9jU2V0IFsvUERGIC9UZXh0IC9JbWFnZUIgL0ltYWdlQyAvSW1hZ2VJXQo+PgplbmRvYmoKNyAwIG9iago8PAovQ3JlYXRpb25EYXRlIChEOjIwMjUxMDA5MTQwODI5WikKPj4KZW5kb2JqCnhyZWYKMCA4CjAwMDAwMDAwMDAgNjU1MzUgZiAKMDAwMDAwMDAwOSAwMDAwMCBuIAowMDAwMDAwMDk2IDAwMDAwIG4gCjAwMDAwMDAxOTkgMDAwMDAgbiAKMDAwMDAwMDI3OSAwMDAwMCBuIAowMDAwMDAwNDE3IDAwMDAwIG4gCjAwMDAwMDAwNiAwMDAwMCBuIAp0cmFpbGVyCjw8Ci9TaXplIDgKL1Jvb3QgMiAwIFIKL0luZm8gNyAwIFIKL0lEIFs8MUI2NDUyRjI2NUU4OTA0NzQ1NERBRURBQzkzNkUyRjA+PDFCNjQ1MkYyNjVFODkwNDc0NTREQUVEQUM5MzZFMkYwPl0KPj4Kc3RhcnR4cmVmCjY2MQolJUVPRgo=',
        isBase64Encoded: true,
      };

      return mockResponse;
    });
  }

  downloadDraft(draftId: number, prid: string): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(
        `${this.baseUrl}/drafts/${draftId}/download?prid=${prid}`,
        {
          params: { draft_id: draftId.toString() },
          observe: 'response',
        }
      )
    ).catch((error) => {
      console.error('API call failed, using mock response:', error);
      // Fallback to mock response if API fails
      const mockResponse = {
        statusCode: 200,
        headers: {
          'Content-Type': 'application/pdf',
          'Content-Disposition': 'attachment; filename="hola.pdf"',
          'Content-Length': '971',
          'Access-Control-Expose-Headers': 'Content-Disposition',
        },
        body: 'JVBERi0xLjMKMSAwIG9iago8PAovQ291bnQgMQovS2lkcyBbMyAwIFJdCi9NZWRpYUJveCBbMCAwIDU5NS4yOCA4NDEuODldCi9UeXBlIC9QYWdlcwo+PgplbmRvYmoKMiAwIG9iago8PAovT3BlbkFjdGlvbiBbMyAwIFIgL0ZpdEggbnVsbF0KL1BhZ2VMYXlvdXQgL09uZUNvbHVtbgovUGFnZXMgMSAwIFIKL1R5cGUgL0NhdGFsb2cKPj4KZW5kb2JqCjMgMCBvYmoKPDwKL0NvbnRlbnRzIDQgMCBSCi9QYXJlbnQgMSAwIFIKL1Jlc291cmNlcyA2IDAgUgovVHlwZSAvUGFnZQo+PgplbmRvYmoKNCAwIG9iago8PAovRmlsdGVyIC9GbGF0ZURlY29kZQovTGVuZ3RoIDY3Cj4+CnN0cmVhbQp4nDNS8OIy0DM1VyjncgpR0HczVDA00zMwUAhJU3ANAQkZWRjqGVoqmBgY6lmaK4SkKGhk5OckaiqEZIEUAACkDg3qCmVuZHN0cmVhbQplbmRvYmoKNSAwIG9iago8PAovQmFzZUZvbnQgL0hlbHZldGljYS1Cb2xkCi9FbmNvZGluZyAvV2luQW5zaUVuY29kaW5nCi9TdWJ0eXBlIC9UeXBlMQovVHlwZSAvRm9udAo+PgplbmRvYmoKNiAwIG9iago8PAovRm9udCA8PC9GMSA1IDAgUj4+Ci9Qcm9jU2V0IFsvUERGIC9UZXh0IC9JbWFnZUIgL0ltYWdlQyAvSW1hZ2VJXQo+PgplbmRvYmoKNyAwIG9iago8PAovQ3JlYXRpb25EYXRlIChEOjIwMjUxMDA5MTQwODI5WikKPj4KZW5kb2JqCnhyZWYKMCA4CjAwMDAwMDAwMDAgNjU1MzUgZiAKMDAwMDAwMDAwOSAwMDAwMCBuIAowMDAwMDAwMDk2IDAwMDAwIG4gCjAwMDAwMDAxOTkgMDAwMDAgbiAKMDAwMDAwMDI3OSAwMDAwMCBuIAowMDAwMDAwNDE3IDAwMDAwIG4gCjAwMDAwMDAwNiAwMDAwMCBuIAp0cmFpbGVyCjw8Ci9TaXplIDgKL1Jvb3QgMiAwIFIKL0luZm8gNyAwIFIKL0lEIFs8MUI2NDUyRjI2NUU4OTA0NzQ1NERBRURBQzkzNkUyRjA+PDFCNjQ1MkYyNjVFODkwNDc0NTREQUVEQUM5MzZFMkYwPl0KPj4Kc3RhcnR4cmVmCjY2MQolJUVPRgo=',
        isBase64Encoded: true,
      };
      // Fallback to mock response if API fails
      return mockResponse;
    });
  }

  /**
   * Search for question options based on question ID and search term
   * @param questionId - The ID of the question
   * @param searchTerm - The search term (2-3 words)
   * @returns Promise with search results
   */
  searchQuestionOptions(
    questionId: number,
    searchTerm: string
  ): Promise<SearchQuestionOptions> {
    const params = new HttpParams()
      .set('question_id', questionId.toString())
      .set('search', searchTerm);

    return lastValueFrom(
      this.http.get<SearchQuestionOptions>(
        `${this.baseUrl}/question-options/search`,
        { params }
      )
    );
  }

  /**
   * Get option mapping by option ID
   * @param optionId - The ID of the selected option
   * @returns Promise with option mapping data
   */
  getOptionMapping(optionId: number): Promise<FormOption> {
    return lastValueFrom(
      this.http.get<FormOption>(`${this.baseUrl}/option-mappings/${optionId}`)
    );
  }

  searchThirdPartyProviders(query: string): Promise<{ providers: ThirdPartyProvider[] }> {
    const params = new HttpParams().set('q', query);
    return lastValueFrom(
      this.http.get<{ providers: ThirdPartyProvider[] }>(
        `${this.baseUrl}/form-options/third-party-providers`,
        { params }
      )
    );
  }

  sendRopaNotificationEmail(emailObject: RopaNotificationEmail): Promise<any> {
    console.log(emailObject);
    return lastValueFrom(
      this.http.post<any>(`${this.baseUrl}/email`, emailObject)
    );
  }

  /**
   *
   * @param request - Upload request with document_type_name and optional comments
   * @returns Presigned POST URL data and document_file_id for confirmation
   */
  generateUploadUrl(request: UploadUrlRequest): Promise<any> {
    return lastValueFrom(
      this.http.post<any>(
        `${this.baseUrl}/documents/generate-upload-url`,
        request
      )
    );
  }

  /**
   * @param file - File to upload
   * @param presignedPost - Presigned POST data from generateUploadUrl
   * @returns Observable with upload progress events
   */
  uploadToS3(file: any, presignedPost: any): Promise<any> {
    const formData = new FormData();

    formData.append('x-amz-server-side-encryption', 'AES256');

    const fieldOrder = [
      'key',
      'x-amz-algorithm',
      'x-amz-credential',
      'x-amz-date',
      'x-amz-security-token',
      'policy',
      'x-amz-signature',
    ];

    console.log('presignedPost fields: ', presignedPost.fields);

    fieldOrder.forEach((key) => {
      if (presignedPost.fields[key] !== undefined) {
        formData.append(key, presignedPost.fields[key]);
      }
    });

    console.log('from data from ropa.service S3 Upload: ', formData);

    formData.append('file', file, file.name);
    return lastValueFrom(
      this.http.post<any>(`${presignedPost.upload_url}`, formData)
    );
  }

  /**
   * Confirm upload completion to change status from pending to completed
   * @param documentFileId - Document file ID to confirm
   * @returns Observable with confirmation response
   */
  confirmUpload(documentFileId: string): Promise<any> {
    return lastValueFrom(
      this.http.post<ConfirmUploadResponse>(
        `${this.baseUrl}/documents/${documentFileId}/confirm`,
        {}
      )
    );
  }

  generateDownloadUrl(documentId: string): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(`${this.baseUrl}/documents/${documentId}/download-url`)
    );
  }

  exportROPAS(SETArea: string, country: string): Promise<any> {
    const encodedCountry = encodeURIComponent(country);
    const encodedSETArea = encodeURIComponent(SETArea);
    
    return lastValueFrom(
      this.http.get<any>(
        `${this.baseUrl}/projects/export/csv?country=${encodedCountry}&set_area=${encodedSETArea}`,
        {
          observe: 'response',
        }
      )
    ).catch((error) => {
      console.error('CSV export API call failed:', error);
      throw error;
    });
  }

  downloadTemplate(): Promise<any> {
    return lastValueFrom(
      this.http.get<any>(`${this.baseUrl}/projects/templates/ingestion`, {
        observe: 'response',
      })
    );
  }
}
